/**
 * AddVehiclePage — vehicle registration form. Mirrors Flutter
 * `add_vehicle_page.dart` (without admin draft auto-save). Photo upload
 * lands in Phase 7 once we wire the upload service.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Truck } from 'lucide-react';
import { useCreateVehicle } from '@/hooks/useVehicles';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import type { VehicleType } from '@/types/trip';

const VEHICLE_TYPES: Array<{ key: VehicleType; label: string }> = [
  { key: 'pickup',       label: 'بيك آب' },
  { key: 'truck',        label: 'شاحنة' },
  { key: 'van',          label: 'فان' },
  { key: 'sedan',        label: 'سيارة عادية' },
  { key: 'motorcycle',   label: 'دراجة نارية' },
  { key: 'refrigerated', label: 'مبرَّدة' },
  { key: 'flatbed',      label: 'مسطَّحة' },
  { key: 'container',    label: 'حاوية' },
  { key: 'other',        label: 'أخرى' },
];

export default function AddVehiclePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateVehicle();
  const [vehicleType, setVehicleType] = useState<VehicleType>('truck');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [color, setColor] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [maxWeightKg, setMaxWeightKg] = useState('');
  const [hasRefrigeration, setHasRefrigeration] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!brand.trim() || !model.trim() || !plateNumber.trim()) {
      setError(t('err_vehicle_required', 'أكمل الماركة والموديل واللوحة')); return;
    }
    try {
      await create.mutateAsync({
        vehicleType,
        brand: brand.trim(),
        model: model.trim(),
        year: year ? parseInt(year, 10) : undefined,
        color: color.trim() || undefined,
        plateNumber: plateNumber.trim(),
        maxWeightKg: maxWeightKg ? parseFloat(maxWeightKg) : undefined,
        hasRefrigeration,
      });
      navigate('/vehicles', { replace: true });
    } catch (err: any) {
      setError(err?.message || t('create_failed', 'فشل الإنشاء'));
    }
  }

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="bg-surface border border-border rounded-2xl p-6 max-w-xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-5 flex items-center gap-2">
          <Truck size={20} /> {t('add_vehicle', 'إضافة مركبة')}
        </h1>

        {error && <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-4">{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              {t('vehicle_type', 'نوع المركبة')}
            </label>
            <div className="flex flex-wrap gap-2">
              {VEHICLE_TYPES.map((vt) => (
                <button
                  key={vt.key}
                  type="button"
                  onClick={() => setVehicleType(vt.key)}
                  className={`px-3 py-1.5 rounded-xl border text-sm transition ${
                    vehicleType === vt.key
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface text-text-secondary border-border hover:border-primary'
                  }`}
                >
                  {t(`vehicle_type.${vt.key}`, vt.label)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TextField label={t('brand', 'الماركة')} value={brand} onChange={(e) => setBrand(e.target.value)} autoFocus />
            <TextField label={t('model', 'الموديل')} value={model} onChange={(e) => setModel(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField label={t('year', 'السنة')} type="number" dir="ltr" value={year} onChange={(e) => setYear(e.target.value)} />
            <TextField label={t('color', 'اللون')} value={color} onChange={(e) => setColor(e.target.value)} />
          </div>
          <TextField
            label={t('plate_number', 'رقم اللوحة')}
            dir="ltr"
            value={plateNumber}
            onChange={(e) => setPlateNumber(e.target.value)}
          />
          <TextField
            label={t('max_weight_kg', 'أقصى حمولة (كغ)')}
            type="number"
            dir="ltr"
            value={maxWeightKg}
            onChange={(e) => setMaxWeightKg(e.target.value)}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={hasRefrigeration}
              onChange={(e) => setHasRefrigeration(e.target.checked)}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm text-text-secondary">{t('has_refrigeration', 'مزوَّدة بتبريد')}</span>
          </label>

          <Button type="submit" loading={create.isPending} fullWidth>
            {t('register_vehicle', 'تسجيل المركبة')}
          </Button>
        </form>
      </div>
    </AppLayout>
  );
}
